'use client';
import React, {
  createContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

export interface PopoverProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  className?: string;
  matchAnchorWidth?: boolean;
  offset?: number;
  animated?: boolean;
  animationDurationMs?: number;
  side?: 'bottom' | 'top' | 'left' | 'right';
}

type Position = { top: number; left: number; width?: number } | null;

type PopoverContextValue = {
  close: () => void;
};

export const PopoverContext = createContext<PopoverContextValue | undefined>(undefined);

export function Popover({
  open,
  onOpenChange,
  anchorRef,
  children,
  className,
  matchAnchorWidth = false,
  offset = 8,
  animated = true,
  animationDurationMs = 150,
  side = 'bottom',
}: PopoverProps) {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<Position>(null);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false); // drives enter/exit animation
  const close = useCallback(() => onOpenChange?.(false), [onOpenChange]);

  const recompute = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const viewportPadding = 8;
    const width: number | undefined = matchAnchorWidth ? rect.width : undefined;
    const contentW = contentRef.current?.offsetWidth || 0;
    const contentH = contentRef.current?.offsetHeight || 0;

    let top = rect.bottom + offset + scrollY;
    let left = rect.left + scrollX;

    switch (side) {
      case 'top':
        top = rect.top - (contentH || 0) - offset + scrollY;
        left = rect.left + scrollX;
        break;
      case 'left':
        left = rect.left - (contentW || 0) - offset + scrollX;
        top = rect.top + scrollY;
        break;
      case 'right':
        left = rect.right + offset + scrollX;
        top = rect.top + scrollY;
        break;
      case 'bottom':
      default:
        top = rect.bottom + offset + scrollY;
        left = rect.left + scrollX;
        break;
    }

    // Clamp within viewport horizontally for top/bottom, using content width so the right edge
    // doesn't overflow the viewport.
    if (side === 'top' || side === 'bottom') {
      const effectiveWidth = width ?? contentW; // width when matching anchor, otherwise content width

      if (effectiveWidth) {
        const minLeft = viewportPadding + scrollX;
        const maxLeft = scrollX + window.innerWidth - viewportPadding - effectiveWidth;
        left = Math.max(minLeft, Math.min(left, maxLeft));
      } else {
        // Fallback if we don't yet know the content width
        left = Math.max(
          viewportPadding + scrollX,
          Math.min(left, window.innerWidth - viewportPadding + scrollX),
        );
      }
    }

    setPos(width !== undefined ? { top, left, width } : { top, left });
  }, [anchorRef, matchAnchorWidth, offset, side]);

  useLayoutEffect(() => {
    if (!mounted) return;
    recompute();
    const onScroll = () => recompute();
    const onResize = () => recompute();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [mounted, recompute]);

  useEffect(() => {
    if (!mounted) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (anchorRef.current?.contains(t)) return;
      if (contentRef.current?.contains(t)) return;
      close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [mounted, anchorRef, close]);

  // Presence + animation lifecycle
  useEffect(() => {
    if (open) {
      // Mount and then animate in next frame
      setMounted(true);
      // Ensure position before showing
      requestAnimationFrame(() => {
        recompute();
        requestAnimationFrame(() => setVisible(true));
      });
    } else if (mounted) {
      // Animate out then unmount
      setVisible(false);
      const t = setTimeout(() => setMounted(false), animated ? animationDurationMs : 0);
      return () => clearTimeout(t);
    }
  }, [open, mounted, animated, animationDurationMs, recompute]);

  if (!mounted || !pos) return null;

  const node = (
    <div
      ref={contentRef}
      className={cn(
        // layout
        'relative z-50',
        // animation
        animated && 'transition ease-out',
        animated && `duration-[${animationDurationMs}ms]`,
        animated &&
          (visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-1 scale-95'),
        className,
      )}
      style={{ position: 'absolute', top: pos.top, left: pos.left, width: pos.width }}
      data-popover-measure
    >
      <PopoverContext.Provider value={{ close }}>{children}</PopoverContext.Provider>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(node, document.body) : null;
}

export default Popover;
